import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import axios from 'axios';
import { 
    MapPin, 
    Plus, 
    Download, 
    Upload, 
    FileText, 
    HelpCircle, 
    Calendar, 
    Link as LinkIcon, 
    Edit, 
    Trash2, 
    Check, 
    FolderOpen, 
    X, 
    ChevronUp, 
    ChevronDown,
    GripVertical,
    Copy,
    Eye,
    EyeOff,
    AlertCircle,
    Search,
    Clock,
    Award,
    ArrowLeft
} from 'lucide-react';
import API_URL, { getUserPlatform } from '../api';
import { useToast } from '../contexts/ToastContext';
import ConfirmModal from './ConfirmModal';
import { copyText } from '../utils/clipboard';
import './QuizBuilder.css';

const createEmptyQuestion = () => ({
    text: '',
    options: ['', '', '', ''],
    correctIndex: 0,
    correctIndices: [0],
    image: '',
    video: '',
    explanation: ''
});

const getQuizCity = (quiz) => quiz?.city || quiz?.targetCity || '';

const cloneQuestion = (question = {}) => {
    let correctIndices = Array.isArray(question.correctIndices)
        ? [...question.correctIndices]
        : [];
    if (correctIndices.length === 0 && Number.isInteger(question.correctIndex)) {
        correctIndices = [question.correctIndex];
    }
    if (correctIndices.length === 0) {
        correctIndices = [0];
    }
    return {
        text: question.text || '',
        options: Array.isArray(question.options) && question.options.length > 0
            ? [...question.options]
            : ['', '', '', ''],
        correctIndex: correctIndices[0] || 0,
        correctIndices,
        image: question.image || '',
        video: question.video || '',
        explanation: question.explanation || ''
    };
};

const cloneQuizForEditing = (quiz = {}) => ({
    ...quiz,
    title: quiz.title || '',
    description: quiz.description || '',
    timeLimit: Number.isFinite(Number(quiz.timeLimit)) ? Number(quiz.timeLimit) : 0,
    passingScore: Number.isFinite(Number(quiz.passingScore)) ? Number(quiz.passingScore) : 80,
    city: getQuizCity(quiz),
    questions: Array.isArray(quiz.questions) && quiz.questions.length > 0
        ? quiz.questions.map(cloneQuestion)
        : [createEmptyQuestion()]
});

const QuizBuilder = () => {
    const toast = useToast();
    const [quizzes, setQuizzes] = useState([]);
    const [editing, setEditing] = useState(null);
    const [collapsedQuestions, setCollapsedQuestions] = useState(new Set());
    const [copyStatus, setCopyStatus] = useState(null);
    const [saving, setSaving] = useState(false);
    const [user, setUser] = useState(null);
    const [cities, setCities] = useState([]);
    const [filterCity, setFilterCity] = useState('');
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, idToDelete: null });

    // QuizBuilder 2.0 States
    const [previewMode, setPreviewMode] = useState(false);
    const [previewStep, setPreviewStep] = useState(0);
    const [previewAnswers, setPreviewAnswers] = useState({});
    const [previewFinished, setPreviewFinished] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const dragItem = useRef(null);
    const dragOverItem = useRef(null);

    useEffect(() => {
        fetchUser();
        fetchQuizzes();
        fetchCities();
    }, []);

    const fetchUser = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_URL}/auth/me`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUser(res.data);
        } catch (e) { console.error(e); }
    };

    const fetchQuizzes = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_URL}/quiz`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setQuizzes((res.data || []).map((quiz) => ({
                ...quiz,
                city: getQuizCity(quiz)
            })));
        } catch (err) {
            console.error('fetchQuizzes:', err);
        }
    };

    const fetchCities = async () => {
        try {
            const res = await axios.get(`${API_URL}/cities${getUserPlatform() ? `?platform=${getUserPlatform()}` : ''}`);
            setCities(res.data);
        } catch (err) { console.error(err); }
    };

    const handleNewQuiz = () => {
        setEditing(cloneQuizForEditing({
            title: '',
            description: '',
            timeLimit: 0,
            passingScore: 80,
            city: '',
            questions: [createEmptyQuestion()]
        }));
        setCollapsedQuestions(new Set());
        setPreviewMode(false);
    };

    const addQuestion = () => {
        const newQs = [...editing.questions, createEmptyQuestion()];
        setEditing((prev) => ({ ...prev, questions: newQs }));
        const newCollapsed = new Set(collapsedQuestions);
        newCollapsed.delete(newQs.length - 1);
        setCollapsedQuestions(newCollapsed);
        
        setTimeout(() => {
            const el = document.getElementById(`q-card-${newQs.length - 1}`);
            if (el) el.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    };

    const removeQuestion = (index) => {
        if (editing.questions.length <= 1) {
            toast.error('Квіз повинен містити щонайменше одне питання');
            return;
        }
        const newQs = editing.questions.filter((_, i) => i !== index);
        setEditing({ ...editing, questions: newQs });
        
        const newCollapsed = new Set();
        collapsedQuestions.forEach(idx => {
            if (idx < index) newCollapsed.add(idx);
            else if (idx > index) newCollapsed.add(idx - 1);
        });
        setCollapsedQuestions(newCollapsed);
    };

    const duplicateQuestion = (index) => {
        const source = editing.questions[index];
        const cloned = cloneQuestion(source);
        cloned.text = cloned.text ? `${cloned.text} (копія)` : 'Копія питання';
        
        const newQs = [...editing.questions];
        newQs.splice(index + 1, 0, cloned);
        setEditing({ ...editing, questions: newQs });
        
        const newCollapsed = new Set();
        collapsedQuestions.forEach(idx => {
            if (idx <= index) newCollapsed.add(idx);
            else newCollapsed.add(idx + 1);
        });
        setCollapsedQuestions(newCollapsed);
        toast.success('Питання продубльовано!');
    };

    const moveQuestion = (index, direction) => {
        const newQs = [...editing.questions];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= newQs.length) return;
        
        const temp = newQs[index];
        newQs[index] = newQs[targetIndex];
        newQs[targetIndex] = temp;
        setEditing({ ...editing, questions: newQs });

        const newCollapsed = new Set();
        collapsedQuestions.forEach(idx => {
            if (idx === index) newCollapsed.add(targetIndex);
            else if (idx === targetIndex) newCollapsed.add(index);
            else newCollapsed.add(idx);
        });
        setCollapsedQuestions(newCollapsed);
    };

    const toggleCollapse = (index) => {
        const newCollapsed = new Set(collapsedQuestions);
        if (newCollapsed.has(index)) newCollapsed.delete(index);
        else newCollapsed.add(index);
        setCollapsedQuestions(newCollapsed);
    };

    const updateQuestion = (index, field, value) => {
        setEditing((prev) => ({
            ...prev,
            questions: prev.questions.map((question, questionIndex) => (
                questionIndex === index ? { ...question, [field]: value } : question
            ))
        }));
    };

    const addOption = (qIdx) => {
        setEditing((prev) => ({
            ...prev,
            questions: prev.questions.map((question, questionIndex) => (
                questionIndex === qIdx
                    ? { ...question, options: [...question.options, ''] }
                    : question
            ))
        }));
    };

    const updateOption = (qIdx, oIdx, value) => {
        setEditing((prev) => ({
            ...prev,
            questions: prev.questions.map((question, questionIndex) => (
                questionIndex === qIdx
                    ? {
                        ...question,
                        options: question.options.map((option, optionIndex) => (
                            optionIndex === oIdx ? value : option
                        ))
                    }
                    : question
            ))
        }));
    };

    const removeOption = (qIdx, oIdx) => {
        setEditing((prev) => ({
            ...prev,
            questions: prev.questions.map((question, questionIndex) => {
                if (questionIndex !== qIdx) return question;

                const nextOptions = question.options.filter((_, optionIndex) => optionIndex !== oIdx);
                let nextCorrectIndices = (question.correctIndices || [question.correctIndex || 0])
                    .filter((idx) => idx !== oIdx)
                    .map((idx) => (idx > oIdx ? idx - 1 : idx));
                
                if (nextCorrectIndices.length === 0) {
                    nextCorrectIndices = [0];
                }

                return {
                    ...question,
                    options: nextOptions,
                    correctIndices: nextCorrectIndices,
                    correctIndex: nextCorrectIndices[0]
                };
            })
        }));
    };

    // Drag and drop sorting handlers
    const handleDragStart = (e, position) => {
        dragItem.current = position;
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, position) => {
        e.preventDefault();
        dragOverItem.current = position;
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const copyListItems = [...editing.questions];
        const dragItemContent = copyListItems[dragItem.current];
        copyListItems.splice(dragItem.current, 1);
        copyListItems.splice(dragOverItem.current, 0, dragItemContent);
        
        // Re-align collapsed items indices
        const oldCollapsed = Array.from(collapsedQuestions);
        const newCollapsed = new Set();
        oldCollapsed.forEach(idx => {
            let nextIdx = idx;
            if (idx === dragItem.current) {
                nextIdx = dragOverItem.current;
            } else if (dragItem.current < dragOverItem.current) {
                if (idx > dragItem.current && idx <= dragOverItem.current) {
                    nextIdx = idx - 1;
                }
            } else if (dragItem.current > dragOverItem.current) {
                if (idx >= dragOverItem.current && idx < dragItem.current) {
                    nextIdx = idx + 1;
                }
            }
            newCollapsed.add(nextIdx);
        });

        dragItem.current = null;
        dragOverItem.current = null;
        setEditing({ ...editing, questions: copyListItems });
        setCollapsedQuestions(newCollapsed);
    };

    const handleFileUpload = async (qIdx, field, file) => {
        if (!file) return;
        
        const isImage = file.type.startsWith('image/');
        const isVideo = file.type.startsWith('video/');
        if (!isImage && !isVideo) {
            toast.error('Будь ласка, виберіть зображення або відео');
            return;
        }
        
        const formData = new FormData();
        formData.append('file', file);

        try {
            const token = localStorage.getItem('token');
            const res = await axios.post(`${API_URL}/upload`, formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            updateQuestion(qIdx, field, res.data.url);
            toast.success('Файл успішно завантажено!');
        } catch (err) {
            console.error('handleFileUpload:', err);
            toast.error('Помилка: ' + (err.response?.data?.error || err.message));
        }
    };

    const isQuestionValid = (q) => {
        const text = String(q.text || '').trim();
        const activeOptions = (q.options || []).map(opt => String(opt || '').trim()).filter(Boolean);
        const correctIndices = q.correctIndices || [];
        return text.length > 0 && activeOptions.length >= 2 && correctIndices.length > 0;
    };

    const handleSave = async () => {
        if (!editing.title.trim()) { toast.error('Введіть назву квізу'); return; }

        if (editing.passingScore !== '' && editing.passingScore !== undefined && editing.passingScore !== null) {
            const ps = Number(editing.passingScore);
            if (isNaN(ps) || ps < 0 || ps > 100) {
                toast.error('Прохідний бал має бути в межах від 0 до 100');
                return;
            }
        }

        if (editing.timeLimit !== '' && editing.timeLimit !== undefined && editing.timeLimit !== null) {
            const tl = Number(editing.timeLimit);
            if (isNaN(tl) || tl < 0 || tl > 1440) {
                toast.error('Час на проходження має бути від 0 до 1440 хвилин');
                return;
            }
        }

        // Client-side validation & normalization of questions
        const sanitizedQuestions = [];
        for (let i = 0; i < editing.questions.length; i++) {
            const q = editing.questions[i];
            const qNum = i + 1;
            
            const text = String(q.text || '').trim();
            if (!text) {
                toast.error(`Питання #${qNum} має містити текст`);
                return;
            }

            const rawOptions = Array.isArray(q.options) ? q.options : [];
            const activeOptions = rawOptions
                .map(opt => String(opt || '').trim())
                .filter(Boolean);

            if (activeOptions.length < 2) {
                toast.error(`Питання #${qNum} має містити щонайменше 2 варіанти відповіді`);
                return;
            }

            const rawCorrectIndices = Array.isArray(q.correctIndices) ? q.correctIndices : [q.correctIndex || 0];
            const correctTexts = rawCorrectIndices
                .map(idx => String(rawOptions[idx] || '').trim())
                .filter(Boolean);

            const newCorrectIndices = correctTexts
                .map(text => activeOptions.indexOf(text))
                .filter(idx => idx !== -1);

            if (newCorrectIndices.length === 0) {
                toast.error(`Питання #${qNum}: оберіть щонайменше один правильний варіант відповіді, який не є порожнім`);
                return;
            }

            sanitizedQuestions.push({
                text,
                options: activeOptions,
                correctIndices: newCorrectIndices,
                correctIndex: newCorrectIndices[0],
                image: String(q.image || '').trim(),
                video: String(q.video || '').trim(),
                explanation: String(q.explanation || '').trim()
            });
        }

        setSaving(true);
        try {
            const token = localStorage.getItem('token');
            const config = { headers: { Authorization: `Bearer ${token}` } };

            const { targetCity, ...editingWithoutLegacyCity } = editing;
            const payload = {
                ...editingWithoutLegacyCity,
                questions: sanitizedQuestions,
                city: ['superadmin', 'admin', 'trainer'].includes(user?.role) ? getQuizCity(editing) : undefined
            };
            if (!['superadmin', 'admin', 'trainer'].includes(user?.role)) {
                delete payload.city;
            }
            if (editing._id) {
                await axios.put(`${API_URL}/quiz/${editing._id}`, payload, config);
            } else {
                await axios.post(`${API_URL}/quiz`, payload, config);
            }
            fetchQuizzes();
            setEditing(null);
            toast.success('Квіз успішно збережено!');
        } catch (err) {
            console.error('handleSave:', err);
            toast.error('Помилка: ' + (err.response?.data?.error || err.message));
        } finally {
            setSaving(false);
        }
    };

    const handleConfirmDelete = async () => {
        const id = confirmModal.idToDelete;
        if (!id) return;
        setConfirmModal({ isOpen: false, idToDelete: null });
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_URL}/quiz/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchQuizzes();
            toast.success('Квіз успішно видалено');
        } catch (err) {
            console.error('handleDelete error:', err);
            toast.error('Не вдалося видалити квіз');
        }
    };

    const handleDownloadTemplate = () => {
        const rows = [
            ['питання', 'варіант_1', 'варіант_2', 'варіант_3', 'варіант_4', 'правильний_варіант'],
            ['Текст першого питання?', 'Варіант А', 'Варіант Б', 'Варіант В', 'Варіант Г', '1'],
            ['Текст другого питання?', 'Варіант А', 'Варіант Б', 'Варіант В', '', '2'],
        ];
        const ws = XLSX.utils.aoa_to_sheet(rows);
        ws['!cols'] = [
            { wch: 40 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 }
        ];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Питання');
        XLSX.writeFile(wb, 'quiz-template.xlsx');
    };

    const handleImport = (e) => {
        const file = e.target.files[0];
        e.target.value = '';
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (ev) => {
            try {
                const wb = XLSX.read(ev.target.result, { type: 'array' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

                // skip header row
                const dataRows = rows.slice(1).filter(r => r[0] && String(r[0]).trim() !== '');

                if (dataRows.length === 0) {
                    toast.error('Файл порожній або невірний формат');
                    return;
                }

                const questions = dataRows.map((r, index) => {
                    const rawOptions = [r[1], r[2], r[3], r[4]];
                    const options = rawOptions
                        .map(o => String(o || '').trim())
                        .filter(o => o !== '');

                    let correctIndex = parseInt(r[5]);
                    if (isNaN(correctIndex)) {
                        correctIndex = 0;
                    } else {
                        correctIndex = Math.max(0, correctIndex - 1);
                    }

                    return {
                        text: String(r[0] || '').trim(),
                        options: options.length >= 2 ? options : [...options, '', ''].slice(0, 2),
                        correctIndex: Math.min(correctIndex, Math.max(0, options.length - 1)),
                        correctIndices: [Math.min(correctIndex, Math.max(0, options.length - 1))],
                        explanation: String(r[6] || '').trim(),
                        image: '',
                        video: ''
                    };
                });

                setEditing(cloneQuizForEditing({
                    title: file.name.replace(/\.[^.]+$/, ''),
                    description: '',
                    timeLimit: 0,
                    passingScore: 80,
                    city: '',
                    questions
                }));
                setCollapsedQuestions(new Set(questions.map((_, i) => i)));
                setPreviewMode(false);
                toast.success(`Імпортовано ${questions.length} питань`);
            } catch (err) {
                console.error('handleImport:', err);
                toast.error('Помилка читання файлу');
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const handleCopyLink = async (quizId) => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.post(`${API_URL}/quiz/links`, { quizId, usageMode: 'multi' }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const url = `${window.location.origin}/quiz/${res.data.hash}`;
            const copyMethod = await copyText(url);
            setCopyStatus(quizId);
            setTimeout(() => setCopyStatus(null), 2000);
            if (copyMethod === 'prompt') {
                toast.info('Посилання готове. Якщо браузер не скопіював його автоматично, скопіюйте вручну.');
            } else {
                toast.success('Посилання скопійовано!');
            }
        } catch (err) {
            console.error('handleCopyLink:', err);
            toast.error('Помилка при створенні посилання');
        }
    };

    // Live Preview Engine Functions
    const handleStartPreview = () => {
        setPreviewMode(true);
        setPreviewStep(0);
        setPreviewAnswers({});
        setPreviewFinished(false);
    };

    const handlePreviewSelect = (optIdx) => {
        const q = editing.questions[previewStep];
        const isMulti = (q.correctIndices || []).length > 1;
        
        if (isMulti) {
            const current = previewAnswers[previewStep] || [];
            let next;
            if (current.includes(optIdx)) {
                next = current.filter(x => x !== optIdx);
            } else {
                next = [...current, optIdx];
            }
            setPreviewAnswers({ ...previewAnswers, [previewStep]: next });
        } else {
            setPreviewAnswers({ ...previewAnswers, [previewStep]: [optIdx] });
        }
    };

    const calculatePreviewScore = () => {
        let correctCount = 0;
        editing.questions.forEach((q, idx) => {
            const userAns = previewAnswers[idx] || [];
            const targetAns = q.correctIndices || [q.correctIndex || 0];
            
            if (userAns.length === targetAns.length && userAns.every(v => targetAns.includes(v))) {
                correctCount++;
            }
        });
        return {
            correct: correctCount,
            total: editing.questions.length,
            percent: Math.round((correctCount / editing.questions.length) * 100)
        };
    };

    if (editing) {
        const stats = {
            total: editing.questions.length,
            valid: editing.questions.filter(isQuestionValid).length,
            invalid: editing.questions.length - editing.questions.filter(isQuestionValid).length,
            multi: editing.questions.filter(q => (q.correctIndices || []).length > 1).length,
            media: editing.questions.filter(q => q.image || q.video).length
        };

        return (
            <div className="quiz-builder-editor v2">
                <header className="qb-header">
                    <div className="qb-header-info">
                        <div className="qb-header-top-row">
                            <button className="qb-back-arrow-btn" onClick={() => setEditing(null)} title="Назад до списку">
                                <ArrowLeft size={20} />
                            </button>
                            <h1>{editing._id ? 'Редагування квізу' : 'Новий квіз'}</h1>
                        </div>
                        <input
                            className="qb-title-input"
                            value={editing.title}
                            onChange={e => setEditing({ ...editing, title: e.target.value })}
                            placeholder="Назва квізу..."
                        />
                    </div>
                    <div className="qb-header-actions">
                        <button 
                            className={`qb-btn ${previewMode ? 'qb-btn-primary' : 'qb-btn-outline'}`}
                            onClick={previewMode ? () => setPreviewMode(false) : handleStartPreview}
                        >
                            {previewMode ? (
                                <><EyeOff size={16} className="qb-btn-icon-svg" /> Редактор</>
                            ) : (
                                <><Eye size={16} className="qb-btn-icon-svg" /> Попередній перегляд</>
                            )}
                        </button>
                        {!previewMode && (
                            <>
                                <button className="qb-btn qb-btn-secondary" onClick={() => setEditing(null)}>Скасувати</button>
                                <button className="qb-btn qb-btn-primary" onClick={handleSave} disabled={saving}>
                                    <Check size={16} className="qb-btn-icon-svg" /> {saving ? 'Збереження...' : 'Зберегти квіз'}
                                </button>
                            </>
                        )}
                    </div>
                </header>

                {previewMode ? (
                    // 🎮 LIVE PREVIEW MODE
                    <div className="qb-preview-container">
                        <div className="qb-preview-card">
                            <div className="preview-progress-bar">
                                <div className="preview-progress-fill" style={{ width: `${((previewStep + (previewFinished ? 1 : 0)) / editing.questions.length) * 100}%` }} />
                            </div>

                            {!previewFinished ? (
                                <div className="preview-question-body">
                                    <div className="preview-q-meta">
                                        <span>Питання {previewStep + 1} з {editing.questions.length}</span>
                                        {(editing.questions[previewStep].correctIndices || []).length > 1 && (
                                            <span className="badge-multi">Кілька відповідей</span>
                                        )}
                                    </div>
                                    
                                    <h2 className="preview-q-text">{editing.questions[previewStep].text || 'Текст питання відсутній'}</h2>

                                    {editing.questions[previewStep].image && (
                                        <div className="preview-media-box">
                                            <img src={editing.questions[previewStep].image} alt="Media" />
                                        </div>
                                    )}

                                    {editing.questions[previewStep].video && !editing.questions[previewStep].image && (
                                        <div className="preview-media-box">
                                            <video src={editing.questions[previewStep].video} controls />
                                        </div>
                                    )}

                                    <div className="preview-options-list">
                                        {(editing.questions[previewStep].options || []).map((opt, oIdx) => {
                                            if (!opt.trim()) return null;
                                            const selected = (previewAnswers[previewStep] || []).includes(oIdx);
                                            return (
                                                <button 
                                                    key={oIdx} 
                                                    className={`preview-opt-btn ${selected ? 'selected' : ''}`}
                                                    onClick={() => handlePreviewSelect(oIdx)}
                                                >
                                                    <span className="opt-letter">{String.fromCharCode(65 + oIdx)}</span>
                                                    <span className="opt-text">{opt}</span>
                                                </button>
                                            );
                                        })}
                                    </div>

                                    <div className="preview-navigation">
                                        <button 
                                            className="qb-btn qb-btn-secondary" 
                                            onClick={() => setPreviewStep(prev => Math.max(0, prev - 1))}
                                            disabled={previewStep === 0}
                                        >
                                            Назад
                                        </button>
                                        
                                        {previewStep < editing.questions.length - 1 ? (
                                            <button 
                                                className="qb-btn qb-btn-primary" 
                                                onClick={() => setPreviewStep(prev => prev + 1)}
                                                disabled={(previewAnswers[previewStep] || []).length === 0}
                                            >
                                                Далі
                                            </button>
                                        ) : (
                                            <button 
                                                className="qb-btn qb-btn-primary preview-finish-btn" 
                                                onClick={() => setPreviewFinished(true)}
                                                disabled={(previewAnswers[previewStep] || []).length === 0}
                                            >
                                                Завершити
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="preview-results-screen">
                                    <div className="results-emoji">{calculatePreviewScore().percent >= editing.passingScore ? '🎉' : '📝'}</div>
                                    <h2>Результати прев'ю</h2>
                                    <div className="results-percentage" style={{ color: calculatePreviewScore().percent >= editing.passingScore ? '#10b981' : '#ef4444' }}>
                                        {calculatePreviewScore().percent}%
                                    </div>
                                    <p className="results-score-label">
                                        Правильно: <strong>{calculatePreviewScore().correct}</strong> з <strong>{calculatePreviewScore().total}</strong> питань.
                                    </p>
                                    <p className="results-pass-status">
                                        Статус: {calculatePreviewScore().percent >= editing.passingScore ? (
                                            <span className="text-success">Пройдено (мінімум {editing.passingScore}%)</span>
                                        ) : (
                                            <span className="text-danger">Не пройдено (мінімум {editing.passingScore}%)</span>
                                        )}
                                    </p>
                                    <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                                        <button className="qb-btn qb-btn-secondary" onClick={handleStartPreview}>Спробувати ще раз</button>
                                        <button className="qb-btn qb-btn-primary" onClick={() => setPreviewMode(false)}>Повернутись до редактора</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    // ✍️ FULL FEATURED EDITOR MODE
                    <div className="qb-editor-layout">
                        {/* LEFT NAV SIDEBAR */}
                        <aside className="qb-editor-sidebar">
                            <div className="sidebar-stats-box">
                                <h4>Панель квізу</h4>
                                <div className="stats-mini-grid">
                                    <div className="stat-card" title="Всього питань">
                                        <span className="stat-val">{stats.total}</span>
                                        <span className="stat-lbl">Питань</span>
                                    </div>
                                    <div className="stat-card" title="Заповнено коректно">
                                        <span className="stat-val text-success">{stats.valid}</span>
                                        <span className="stat-lbl">Готово</span>
                                    </div>
                                    <div className="stat-card" title="Питання з кількома правильними">
                                        <span className="stat-val text-blue">{stats.multi}</span>
                                        <span className="stat-lbl">Мульти</span>
                                    </div>
                                    <div className="stat-card" title="Питання з медіафайлами">
                                        <span className="stat-val text-yellow">{stats.media}</span>
                                        <span className="stat-lbl">Медіа</span>
                                    </div>
                                </div>
                            </div>

                            <div className="sidebar-search-box">
                                <Search size={16} className="sidebar-search-icon" />
                                <input 
                                    type="text" 
                                    placeholder="Швидкий пошук..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>

                            <div className="sidebar-q-nav-list">
                                <div className="sidebar-q-list-header">Навігація по питаннях</div>
                                {editing.questions.map((q, qIdx) => {
                                    const isValid = isQuestionValid(q);
                                    const active = !collapsedQuestions.has(qIdx);
                                    const matchesSearch = !searchQuery || q.text.toLowerCase().includes(searchQuery.toLowerCase());
                                    
                                    return (
                                        <button 
                                            key={qIdx} 
                                            className={`sidebar-q-nav-btn ${active ? 'active' : ''} ${isValid ? 'valid' : 'invalid'} ${matchesSearch ? '' : 'hidden-search'}`}
                                            onClick={() => {
                                                const newCollapsed = new Set(collapsedQuestions);
                                                newCollapsed.delete(qIdx);
                                                setCollapsedQuestions(newCollapsed);
                                                
                                                setTimeout(() => {
                                                    const el = document.getElementById(`q-card-${qIdx}`);
                                                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                                                }, 100);
                                            }}
                                        >
                                            <span className="q-nav-circle">
                                                {isValid ? <Check size={12} /> : <AlertCircle size={12} />}
                                            </span>
                                            <span className="q-nav-number">#{qIdx + 1}</span>
                                            <span className="q-nav-text-preview">{q.text || 'Питання без тексту'}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </aside>

                        {/* CENTRAL MAIN SCROLL WORKSPACE */}
                        <main className="qb-main">
                            <div className="qb-general-settings glass-panel">
                                <div className="qb-section">
                                    <label className="qb-label">Опис квізу</label>
                                    <textarea
                                        className="qb-textarea"
                                        value={editing.description}
                                        onChange={e => setEditing({ ...editing, description: e.target.value })}
                                        placeholder="Введіть короткий опис для квізу..."
                                        rows={2}
                                    />
                                </div>

                                <div className="qb-row qb-settings-row">
                                    <div className="qb-section">
                                        <label className="qb-label"><Clock size={15} style={{ marginRight: '6px', verticalAlign: 'middle' }} /> Час на проходження (хв)</label>
                                        <input
                                            type="number"
                                            className="qb-input"
                                            value={editing.timeLimit ?? ''}
                                            onChange={e => {
                                                const val = e.target.value;
                                                setEditing({ ...editing, timeLimit: val === '' ? '' : parseInt(val) });
                                            }}
                                            placeholder="0 = без обмеження"
                                        />
                                    </div>
                                    <div className="qb-section">
                                        <label className="qb-label"><Award size={15} style={{ marginRight: '6px', verticalAlign: 'middle' }} /> Прохідний відсоток (%)</label>
                                        <input
                                            type="number"
                                            className="qb-input"
                                            value={editing.passingScore ?? ''}
                                            onChange={e => {
                                                const val = e.target.value;
                                                setEditing({ ...editing, passingScore: val === '' ? '' : parseInt(val) });
                                            }}
                                        />
                                    </div>
                                </div>
                                {['superadmin', 'admin', 'trainer'].includes(user?.role) && (
                                    <div className="qb-city-field">
                                        <span className="qb-city-label"><MapPin size={14} className="qb-btn-icon-svg" /> Місто:</span>
                                        <select
                                            className="qb-input qb-city-select"
                                            value={editing.city || ''}
                                            onChange={e => setEditing({ ...editing, city: e.target.value })}
                                        >
                                            <option value="">Всі міста</option>
                                            {cities.map(c => (
                                                <option key={c._id} value={c.name}>{c.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                            </div>

                            <div className="qb-questions-list">
                                {editing.questions.map((q, qIdx) => {
                                    const isCollapsed = collapsedQuestions.has(qIdx);
                                    const isValid = isQuestionValid(q);
                                    
                                    return (
                                        <div 
                                            key={qIdx} 
                                            id={`q-card-${qIdx}`}
                                            className={`qb-question-card ${isCollapsed ? 'collapsed' : ''} ${isValid ? 'is-valid' : 'is-invalid'}`}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, qIdx)}
                                            onDragOver={(e) => handleDragOver(e, qIdx)}
                                            onDrop={handleDrop}
                                        >
                                            <div className="qb-q-header">
                                                <div className="qb-q-header-left" onClick={() => toggleCollapse(qIdx)}>
                                                    <span className="qb-q-grip-handle" title="Перетягнути для сортування">
                                                        <GripVertical size={16} />
                                                    </span>
                                                    <span className="qb-q-number">#{qIdx + 1}</span>
                                                    {isCollapsed && <span className="qb-q-preview">{q.text || 'Питання без тексту'}</span>}
                                                    {!isValid && isCollapsed && <span className="warning-dot" title="Не всі поля заповнені" />}
                                                </div>
                                                <div className="qb-q-actions">
                                                    <button className="qb-q-btn-icon" onClick={() => moveQuestion(qIdx, 'up')} disabled={qIdx === 0} title="Перемістити вгору"><ChevronUp size={16} /></button>
                                                    <button className="qb-q-btn-icon" onClick={() => moveQuestion(qIdx, 'down')} disabled={qIdx === editing.questions.length - 1} title="Перемістити вниз"><ChevronDown size={16} /></button>
                                                    <button className="qb-q-btn-icon" onClick={() => duplicateQuestion(qIdx)} title="Копіювати питання"><Copy size={16} /></button>
                                                    <button className="qb-q-remove" onClick={() => removeQuestion(qIdx)} title="Видалити питання"><Trash2 size={16} /></button>
                                                </div>
                                            </div>

                                            {!isCollapsed && (
                                                <div className="qb-q-body-content">
                                                    <input
                                                        className="qb-input qb-q-text"
                                                        value={q.text}
                                                        onChange={e => updateQuestion(qIdx, 'text', e.target.value)}
                                                        placeholder="Текст питання..."
                                                    />

                                                    <div className="qb-row qb-media-row">
                                                        <div className="qb-section">
                                                            <label className="qb-label">Зображення</label>
                                                            <div className="file-upload-wrapper">
                                                                <input
                                                                    className="qb-input qb-media-input"
                                                                    value={q.image || ''}
                                                                    onChange={e => updateQuestion(qIdx, 'image', e.target.value)}
                                                                    placeholder="URL зображення..."
                                                                />
                                                                <label className="file-upload-btn">
                                                                    <FolderOpen size={16} />
                                                                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleFileUpload(qIdx, 'image', e.target.files[0])} />
                                                                </label>
                                                            </div>
                                                        </div>
                                                        <div className="qb-section">
                                                            <label className="qb-label">Відео</label>
                                                            <div className="file-upload-wrapper">
                                                                <input
                                                                    className="qb-input qb-media-input"
                                                                    value={q.video || ''}
                                                                    onChange={e => updateQuestion(qIdx, 'video', e.target.value)}
                                                                    placeholder="URL відео..."
                                                                />
                                                                <label className="file-upload-btn">
                                                                    <FolderOpen size={16} />
                                                                    <input type="file" accept="video/*" style={{ display: 'none' }} onChange={e => handleFileUpload(qIdx, 'video', e.target.files[0])} />
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="qb-options-grid">
                                                        {q.options.map((opt, oIdx) => (
                                                            <div key={oIdx} className={`qb-option-item ${(q.correctIndices || []).includes(oIdx) ? 'correct' : ''}`}>
                                                                <input
                                                                    type="checkbox"
                                                                    className="qb-checkbox"
                                                                    checked={(q.correctIndices || []).includes(oIdx)}
                                                                    onChange={() => {
                                                                        const currentIndices = q.correctIndices || [];
                                                                        let nextIndices;
                                                                        if (currentIndices.includes(oIdx)) {
                                                                            if (currentIndices.length > 1) {
                                                                                nextIndices = currentIndices.filter(idx => idx !== oIdx);
                                                                            } else {
                                                                                return;
                                                                            }
                                                                        } else {
                                                                            nextIndices = [...currentIndices, oIdx];
                                                                        }
                                                                        nextIndices.sort((a, b) => a - b);
                                                                        updateQuestion(qIdx, 'correctIndices', nextIndices);
                                                                        updateQuestion(qIdx, 'correctIndex', nextIndices[0]);
                                                                    }}
                                                                />
                                                                <input
                                                                    className="qb-input-mini"
                                                                    value={opt}
                                                                    onChange={e => updateOption(qIdx, oIdx, e.target.value)}
                                                                    placeholder={`Варіант ${oIdx + 1}`}
                                                                />
                                                                {q.options.length > 2 && (
                                                                    <button className="qb-opt-remove" onClick={() => removeOption(qIdx, oIdx)}><X size={14} /></button>
                                                                )}
                                                            </div>
                                                        ))}
                                                        <button className="qb-btn-add-opt" onClick={() => addOption(qIdx)}><Plus size={14} className="qb-btn-icon-svg" /> Додати варіант відповіді</button>
                                                    </div>

                                                    <div className="qb-section qb-explanation-section" style={{ marginTop: '1.5rem' }}>
                                                        <label className="qb-label">Пояснення правильної відповіді (необов'язково)</label>
                                                        <textarea
                                                            className="qb-textarea"
                                                            value={q.explanation || ''}
                                                            onChange={e => updateQuestion(qIdx, 'explanation', e.target.value)}
                                                            placeholder="Поясніть студенту, чому цей варіант правильний..."
                                                            rows={2}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}

                                <div className="qb-add-question-bottom">
                                    <button className="qb-btn qb-btn-primary qb-add-q-btn" onClick={addQuestion}>
                                        <Plus size={20} className="qb-btn-icon-svg" /> Додати нове питання
                                    </button>
                                </div>
                            </div>
                        </main>
                    </div>
                )}
            </div>
        );
    }

    const filteredQuizzes = quizzes.filter(q =>
        !filterCity || getQuizCity(q) === filterCity
    );

    return (
        <div className="quiz-builder">
            <header className="quiz-header">
                <div className="quiz-header-info">
                    <h1>Квізи (Тести)</h1>
                    <p>Керуйте текстовими тестами для персоналу</p>
                </div>
                <div className="quiz-header-actions">
                    {['superadmin', 'admin', 'trainer'].includes(user?.role) && (
                        <div className="city-filter-container">
                            <span className="city-filter-label">📍 Місто:</span>
                            <select
                                value={filterCity}
                                onChange={e => setFilterCity(e.target.value)}
                            >
                                <option value="">Всі міста</option>
                               {cities.map(c => (
                                    <option key={c._id} value={c.name}>{c.name}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    <button className="qb-btn qb-btn-outline" onClick={handleDownloadTemplate} title="Завантажити Excel-шаблон">
                        <Download size={18} className="qb-btn-icon-svg" /> Шаблон
                    </button>
                    <label className="qb-btn qb-btn-outline qb-import-label" title="Імпортувати з Excel">
                        <Upload size={18} className="qb-btn-icon-svg" /> Імпорт
                        <input type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleImport} />
                    </label>
                    <button className="qb-btn qb-btn-primary" onClick={handleNewQuiz}>
                        <Plus size={18} className="qb-btn-icon-svg" /> Створити квіз
                    </button>
                </div>
            </header>

            <div className="quiz-grid">
                {filteredQuizzes.length === 0 ? (
                    <div className="qb-empty">
                        <span className="qb-empty-icon"><FileText size={48} /></span>
                        <h3>Немає створених квізів</h3>
                        <p>Натисніть кнопку вище, щоб створити свій перший тест</p>
                    </div>
                ) : (
                    filteredQuizzes.map(quiz => (
                        <div key={quiz._id} className="quiz-card">
                            <div className="quiz-card-content">
                                <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                    {quiz.title}
                                    {getQuizCity(quiz) && (
                                        <span className="quiz-card-city-badge">
                                            <MapPin size={12} /> {getQuizCity(quiz)}
                                        </span>
                                    )}
                                </h3>
                                <p>{quiz.description || 'Без опису'}</p>
                                <div className="quiz-meta">
                                    <span><HelpCircle size={14} /> {quiz.questions.length} питань</span>
                                    <span><Calendar size={14} /> {new Date(quiz.createdAt).toLocaleDateString()}</span>
                                </div>
                            </div>
                            <div className="quiz-card-actions">
                                <button className="qb-btn qb-btn-secondary" onClick={() => handleCopyLink(quiz._id)}>
                                    {copyStatus === quiz._id ? <><Check size={16} /> Скопійовано</> : <><LinkIcon size={16} /> Посилання</>}
                                </button>
                                <button className="qb-btn-icon" onClick={() => setEditing(cloneQuizForEditing(quiz))} title="Редагувати"><Edit size={18} /></button>
                                <button className="qb-btn-icon delete" onClick={() => setConfirmModal({ isOpen: true, idToDelete: quiz._id })} title="Видалити"><Trash2 size={18} /></button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            <ConfirmModal
                isOpen={confirmModal.isOpen}
                title="Видалення квізу"
                message="Ви впевнені, що хочете видалити цей квіз? Цю дію неможливо скасувати."
                confirmText="Видалити"
                onConfirm={handleConfirmDelete}
                onCancel={() => setConfirmModal({ isOpen: false, idToDelete: null })}
            />
        </div>
    );
};

export default QuizBuilder;
